/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  search: {
    all: ["search"] as const,
    results: (query: string, filter: string) => ["search", "results", query, filter] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (clear-data button in profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; profile + list-dialog SHARE the cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated).*/
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user.*/
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id).*/
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme).*/
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  // ── Jornada — WORKFORCE domain ───────────────────────────────────────────────
  workforce: {
    all: ["workforce"] as const,
    /** Employee profiles (mine + those of my organizations). */
    profiles: (userId: string | undefined) => ["workforce", "profiles", userId] as const,
    /** A specific profile (detail / transparency). */
    profile: (profileId: string | undefined) => ["workforce", "profile", profileId] as const,
    /** Active seconds aggregated by category for a profile. */
    usageByCategory: (profileId: string | undefined) => ["workforce", "usage", profileId] as const,
    /** Proposed time entries for a profile (optionally by status). */
    proposed: (profileId: string | undefined, status?: string) =>
      ["workforce", "proposed", profileId, status] as const,
    /** Proposed time entries for an employee (subject) by status. */
    proposedBySubject: (subjectId: string | undefined, status?: string) =>
      ["workforce", "proposed-subject", subjectId, status] as const,
    /** Attribution by project for a profile. */
    attribution: (profileId: string | undefined) =>
      ["workforce", "attribution", profileId] as const,
    /** Transparency read (disclosure/consent/schedule) for a profile. */
    transparency: (profileId: string | undefined) =>
      ["workforce", "transparency", profileId] as const,
    /** Number of proposed entries pending review (overview badge). */
    pendingCount: (userId: string | undefined) => ["workforce", "pending", userId] as const,
    /** Devices enrolled in the agent datapath (API-only). */
    devices: (profileId: string | undefined) => ["workforce", "devices", profileId] as const,
  },
  // ── Jornada — opt-in personal-productivity gamification ────────────────────────
  gamification: {
    all: ["gamification"] as const,
    /** Minimized telemetry that derives the player's game state. */
    player: (userId: string | undefined) => ["gamification", "player", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences).*/
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector).*/
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector).*/
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all?*/
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync).*/
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists.*/
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters.*/
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  organizations: {
    /** Local replica (PowerSync) of my organizations. */
    localList: () => ["organizations", "local-list"] as const,
    /** Members (with public info) of an organization, from the local replica. */
    localMembers: (organizationId: string | undefined) =>
      ["organizations", "local-members", organizationId] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    /**
     * List of devices with an open session. Two entries on purpose:
     * `activeSessions()` is the PREFIX used for invalidation (after revocation), and
     * `activeSessionsFor()` is the concrete key used by the screen. A single
     * getter with an optional id would not work: `["active-sessions", undefined]`
     * does NOT match by prefix with `["active-sessions", "<id>"]`.
     */
    activeSessions: () => ["active-sessions"] as const,
    activeSessionsFor: (sessionId: string | undefined) => ["active-sessions", sessionId] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
